import type { ChatMessage } from '@skychat/shared-core';

const DATABASE_NAME = 'skychat-mobile-reliability';
const DATABASE_VERSION = 1;
const OUTBOX_STORE = 'socket-outbox';
const UPLOAD_STORE = 'pending-uploads';

export interface PersistedSocketOutboxEntry {
  key: string;
  owner: string;
  peerId: string;
  message: ChatMessage;
  authRetryCount: number;
  attemptCount: number;
  createdAt: number;
  updatedAt: number;
}

export type PendingUploadMode = 'server' | 'p2p';

interface PersistedUploadEntry {
  key: string;
  owner: string;
  messageId: string;
  peerId: string;
  blob: Blob;
  name: string;
  type: string;
  lastModified: number;
  createdAt: number;
  updatedAt: number;
  mode?: PendingUploadMode;
}

type ReliabilityRecord = PersistedSocketOutboxEntry | PersistedUploadEntry;

const memoryFallback = new Map<string, Map<string, ReliabilityRecord>>([
  [OUTBOX_STORE, new Map()],
  [UPLOAD_STORE, new Map()]
]);
const deletionTombstones = new Map<string, Set<string>>([
  [OUTBOX_STORE, new Set()],
  [UPLOAD_STORE, new Set()]
]);
const ownerClearRevision = new Map<string, Map<string, number>>([
  [OUTBOX_STORE, new Map()],
  [UPLOAD_STORE, new Map()]
]);

let databasePromise: Promise<IDBDatabase | null> | null = null;
let mutationTail: Promise<void> = Promise.resolve();

function reliabilityKey(owner: string, id: string): string {
  return `${owner}:${id}`;
}

function fallbackStore(storeName: string): Map<string, ReliabilityRecord> {
  let store = memoryFallback.get(storeName);
  if (!store) {
    store = new Map();
    memoryFallback.set(storeName, store);
  }
  return store;
}

function tombstonesFor(storeName: string): Set<string> {
  let tombstones = deletionTombstones.get(storeName);
  if (!tombstones) {
    tombstones = new Set();
    deletionTombstones.set(storeName, tombstones);
  }
  return tombstones;
}

function clearedOwnersFor(storeName: string): Map<string, number> {
  let owners = ownerClearRevision.get(storeName);
  if (!owners) {
    owners = new Map();
    ownerClearRevision.set(storeName, owners);
  }
  return owners;
}

function indexedDbAvailable(): boolean {
  return typeof indexedDB !== 'undefined';
}

/**
 * Keep one database handle for the lifetime of the WebView. Repeated open/close
 * cycles are surprisingly fragile on iOS after a process suspension and can
 * also reorder an ACK delete behind an older outbox write.
 */
function openReliabilityDatabase(): Promise<IDBDatabase | null> {
  if (!indexedDbAvailable()) return Promise.resolve(null);
  if (databasePromise) return databasePromise;

  databasePromise = new Promise((resolve) => {
    try {
      const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
      request.onupgradeneeded = () => {
        const database = request.result;
        if (!database.objectStoreNames.contains(OUTBOX_STORE)) {
          const store = database.createObjectStore(OUTBOX_STORE, { keyPath: 'key' });
          store.createIndex('owner', 'owner', { unique: false });
        }
        if (!database.objectStoreNames.contains(UPLOAD_STORE)) {
          const store = database.createObjectStore(UPLOAD_STORE, { keyPath: 'key' });
          store.createIndex('owner', 'owner', { unique: false });
        }
      };
      request.onsuccess = () => {
        const database = request.result;
        database.onversionchange = () => {
          database.close();
          databasePromise = null;
        };
        resolve(database);
      };
      request.onerror = () => {
        databasePromise = null;
        resolve(null);
      };
      request.onblocked = () => {
        databasePromise = null;
        resolve(null);
      };
    } catch {
      databasePromise = null;
      resolve(null);
    }
  });
  return databasePromise;
}

function enqueueMutation(operation: () => Promise<void>): Promise<void> {
  const queued = mutationTail.catch(() => undefined).then(operation, operation);
  mutationTail = queued.catch(() => undefined);
  return queued;
}

async function waitForMutations(): Promise<void> {
  await mutationTail.catch(() => undefined);
}

async function writeRecord(storeName: string, record: ReliabilityRecord): Promise<void> {
  await enqueueMutation(async () => {
    // The memory mirror is both an immediate read-after-write guarantee and a
    // session fallback when private browsing or the native WebView denies IDB.
    tombstonesFor(storeName).delete(record.key);
    fallbackStore(storeName).set(record.key, record);
    const database = await openReliabilityDatabase();
    if (!database) return;
    await new Promise<void>((resolve) => {
      try {
        const transaction = database.transaction(storeName, 'readwrite');
        transaction.objectStore(storeName).put(record);
        transaction.oncomplete = () => resolve();
        transaction.onerror = () => resolve();
        transaction.onabort = () => resolve();
      } catch {
        resolve();
      }
    });
  });
}

async function deleteRecord(storeName: string, key: string): Promise<void> {
  await enqueueMutation(async () => {
    fallbackStore(storeName).delete(key);
    tombstonesFor(storeName).add(key);
    const database = await openReliabilityDatabase();
    if (!database) return;
    await new Promise<void>((resolve) => {
      try {
        const transaction = database.transaction(storeName, 'readwrite');
        transaction.objectStore(storeName).delete(key);
        transaction.oncomplete = () => resolve();
        transaction.onerror = () => resolve();
        transaction.onabort = () => resolve();
      } catch {
        resolve();
      }
    });
  });
}

async function listOwnerRecords<T extends ReliabilityRecord>(storeName: string, owner: string): Promise<T[]> {
  await waitForMutations();
  const merged = new Map<string, T>();
  const tombstones = tombstonesFor(storeName);
  const clearedAt = clearedOwnersFor(storeName).get(owner) || 0;
  for (const record of fallbackStore(storeName).values()) {
    if (record.owner === owner && !tombstones.has(record.key)) merged.set(record.key, record as T);
  }

  const database = await openReliabilityDatabase();
  if (!database) return [...merged.values()];
  const records = await new Promise<T[]>((resolve) => {
    try {
      const transaction = database.transaction(storeName, 'readonly');
      const store = transaction.objectStore(storeName);
      const index = store.index('owner');
      const request = index.getAll(owner);
      request.onsuccess = () => resolve(Array.isArray(request.result) ? request.result as T[] : []);
      request.onerror = () => resolve([]);
    } catch {
      resolve([]);
    }
  });
  for (const record of records) {
    // In-memory entries and tombstones are newer than the persistent snapshot
    // when an IDB transaction failed.
    if (tombstones.has(record.key)) continue;
    if (clearedAt > 0 && Number(record.updatedAt || 0) <= clearedAt) continue;
    if (!merged.has(record.key)) merged.set(record.key, record);
  }
  return [...merged.values()];
}

async function clearOwnerRecords(storeName: string, owner: string): Promise<void> {
  await enqueueMutation(async () => {
    const clearedAt = Date.now();
    clearedOwnersFor(storeName).set(owner, clearedAt);
    const memoryStore = fallbackStore(storeName);
    for (const [key, record] of memoryStore) {
      if (record.owner === owner) memoryStore.delete(key);
    }

    const database = await openReliabilityDatabase();
    if (!database) return;
    await new Promise<void>((resolve) => {
      try {
        const transaction = database.transaction(storeName, 'readwrite');
        const store = transaction.objectStore(storeName);
        const index = store.index('owner');
        const request = index.openKeyCursor(IDBKeyRange.only(owner));
        request.onsuccess = () => {
          const cursor = request.result;
          if (!cursor) return;
          store.delete(cursor.primaryKey);
          cursor.continue();
        };
        transaction.oncomplete = () => resolve();
        transaction.onerror = () => resolve();
        transaction.onabort = () => resolve();
      } catch {
        resolve();
      }
    });
  });
}

export async function persistSocketOutboxEntry(
  owner: string,
  entry: Omit<PersistedSocketOutboxEntry, 'key' | 'owner' | 'updatedAt'>
): Promise<void> {
  if (!owner || !entry.message?.id) return;
  await writeRecord(OUTBOX_STORE, {
    ...entry,
    key: reliabilityKey(owner, entry.message.id),
    owner,
    updatedAt: Date.now()
  } satisfies PersistedSocketOutboxEntry);
}

export async function removePersistedSocketOutboxEntry(owner: string, messageId: string): Promise<void> {
  if (!owner || !messageId) return;
  await deleteRecord(OUTBOX_STORE, reliabilityKey(owner, messageId));
}

export async function listPersistedSocketOutbox(owner: string): Promise<PersistedSocketOutboxEntry[]> {
  if (!owner) return [];
  return (await listOwnerRecords<PersistedSocketOutboxEntry>(OUTBOX_STORE, owner))
    .filter((entry) => !!entry.message?.id && !!entry.peerId)
    .sort((a, b) => a.createdAt - b.createdAt);
}

export async function persistPendingUpload(
  owner: string,
  messageId: string,
  peerId: string,
  file: File,
  mode: PendingUploadMode = 'server'
): Promise<void> {
  if (!owner || !messageId || !peerId || !file) return;
  await writeRecord(UPLOAD_STORE, {
    key: reliabilityKey(owner, messageId),
    owner,
    messageId,
    peerId,
    blob: file,
    name: file.name,
    type: file.type,
    lastModified: file.lastModified || Date.now(),
    createdAt: Date.now(),
    updatedAt: Date.now(),
    mode
  } satisfies PersistedUploadEntry);
}

export async function restorePendingUpload(owner: string, messageId: string): Promise<{ peerId: string; file: File; mode: PendingUploadMode } | null> {
  if (!owner || !messageId) return null;
  const entries = await listOwnerRecords<PersistedUploadEntry>(UPLOAD_STORE, owner);
  const entry = entries.find((candidate) => candidate.messageId === messageId);
  if (!entry?.blob) return null;
  try {
    return {
      peerId: entry.peerId,
      mode: entry.mode === 'p2p' ? 'p2p' : 'server',
      file: new File([entry.blob], entry.name || 'upload.bin', {
        type: entry.type || entry.blob.type || 'application/octet-stream',
        lastModified: entry.lastModified || Date.now()
      })
    };
  } catch {
    return null;
  }
}

export async function listPendingUploads(owner: string): Promise<Array<{ messageId: string; peerId: string; file: File; mode: PendingUploadMode; createdAt: number }>> {
  if (!owner) return [];
  const entries = await listOwnerRecords<PersistedUploadEntry>(UPLOAD_STORE, owner);
  return entries.flatMap((entry) => {
    if (!entry?.blob || !entry.messageId || !entry.peerId) return [];
    try {
      return [{
        messageId: entry.messageId,
        peerId: entry.peerId,
        mode: entry.mode === 'p2p' ? 'p2p' : 'server',
        createdAt: entry.createdAt || Date.now(),
        file: new File([entry.blob], entry.name || 'upload.bin', {
          type: entry.type || entry.blob.type || 'application/octet-stream',
          lastModified: entry.lastModified || Date.now()
        })
      }];
    } catch {
      return [];
    }
  });
}

export async function removePendingUpload(owner: string, messageId: string): Promise<void> {
  if (!owner || !messageId) return;
  await deleteRecord(UPLOAD_STORE, reliabilityKey(owner, messageId));
}

export async function clearMobileReliabilityState(owner: string): Promise<void> {
  if (!owner) return;
  // Keep owner cleanup ordered after all prior per-message writes/deletes.
  await clearOwnerRecords(OUTBOX_STORE, owner);
  await clearOwnerRecords(UPLOAD_STORE, owner);
}
